import React from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { adminApi } from '../../api/client';
import { useQuery } from '@tanstack/react-query';
import { getApiErrorMessage } from '../../api/errors';

function useAdminSubscriptions() {
  return useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: async () => {
      const { data } = await adminApi.getSubscriptions(1, 50);
      if (!data.success) throw new Error(data.message || 'Failed to load subscriptions');
      return data.data || [];
    },
  });
}

const STATUS_STYLES = {
  Active: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25',
  Pending: 'bg-[#f0a818]/10 text-[#f0a818] ring-[#f0a818]/30',
  Expired: 'bg-white/5 text-muted-foreground ring-white/10',
  Failed: 'bg-destructive/10 text-destructive ring-destructive/25',
};

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || 'bg-white/5 text-muted-foreground ring-white/10';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${style}`}>
      {status}
    </span>
  );
}

function StatCard({ icon, value, label }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06]">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-primary ring-1 ring-white/10">
        <Icon icon={icon} className="size-4" />
      </span>
      <div className="min-w-0">
        <div className="truncate text-lg font-semibold text-foreground">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export default function AdminSubscriptionsPage() {
  usePageTitle('Subscriptions · Kast');
  const { data: subs = [], isLoading, error } = useAdminSubscriptions();

  const activeCount = subs.filter((s) => s.IsActive).length;
  const totalRevenue = subs
    .filter((s) => s.Status === 'Active' || s.Status === 'Expired')
    .reduce((sum, s) => sum + (s.AmountPaid || 0), 0);

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
            <Icon icon="ph:receipt-bold" className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Subscriptions</h1>
            <p className="text-xs text-muted-foreground">Track subscriber payments and status</p>
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard icon="ph:check-circle-bold" value={activeCount} label="Active" />
        <StatCard icon="ph:users-three-bold" value={subs.length} label="Total" />
        <StatCard icon="ph:currency-circle-dollar-bold" value={`₦${totalRevenue.toLocaleString()}`} label="Revenue" />
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : error ? (
        <EmptyState icon="ph:warning-bold">{getApiErrorMessage(error, 'Failed to load subscriptions.')}</EmptyState>
      ) : subs.length === 0 ? (
        <EmptyState icon="ph:receipt-bold">No subscriptions yet</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl ring-1 ring-foreground/[0.06]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/5 text-xs uppercase tracking-wider text-muted-foreground/60">
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Start</th>
                <th className="px-4 py-3 font-medium">Expiry</th>
                <th className="px-4 py-3 font-medium">Reference</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.Id} className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground">{s.UserName}</span>
                      <span className="text-xs text-muted-foreground">{s.UserEmail}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground">₦{s.AmountPaid?.toLocaleString()}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.Status} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{s.StartDate?.split('T')[0]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.ExpiryDate?.split('T')[0]}</td>
                  <td className="px-4 py-3">
                    <code className="rounded bg-white/5 px-1.5 py-0.5 text-xs text-muted-foreground">{s.PaystackReference}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
